import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { FC, useEffect, useRef } from 'react';
import {
	emitFBEventSetUserId,
	emitGAEventSetUserId
} from '../../../utils/ga-events-helpers';
import { cookieCountries } from '../../modules/Constants';

const FBPixelScript: FC<{ pixelId?: string; externalId?: string }> = ({ pixelId, externalId }) => {
	if (!pixelId) return null;
	return (
		<>
			<Head>
				<meta name="facebook-domain-verification" content="hulvbwbh4casep7nigfu3ecpa2rwor" />
			</Head>
			<Script id="fb-pixel" strategy="afterInteractive">
				{`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${pixelId}', ${externalId ? `{ external_id: '${externalId}' }` : '{}'});
          fbq('track', 'PageView');
        `}
			</Script>
			<noscript>
				<img
					height="1"
					width="1"
					style={{ display: 'none' }}
					src={`https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1${externalId ? `&external_id=${externalId}` : ''}`}
					alt=""
				/>
			</noscript>
		</>
	);
};

const GA_MEASUREMENT_ID = 'G-5X8N5YDNDK';

const RenderGA: FC<{ isSiteLoaded: boolean; enabledVert?: string; uid?: string; imid?: string }> = ({
	isSiteLoaded,
	enabledVert,
	uid,
	imid,
}) => {
	const router = useRouter();
	const pageViewFiredRef = useRef(false);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			window.dataLayer = window.dataLayer || [];
			window.gtag =
				window.gtag ||
				function () {
					// eslint-disable-next-line prefer-rest-params
					(window.dataLayer = window.dataLayer || []).push(arguments);
				};

			window.gtag('consent', 'default', {
				functionality_storage: 'denied',
				security_storage: 'granted',
				ad_personalization: 'denied',
				ad_storage: 'denied',
				ad_user_data: 'denied',
				analytics_storage: 'denied',
				personalization_storage: 'denied',
				region: cookieCountries,
			});

			window.gtag('set', 'ads_data_redaction', true);
			window.gtag('set', 'url_passthrough', false);

			// Hand-rolled gtag bootstrap. send_page_view:false suppresses the auto page_view so we
			// can fire it by hand with the current-session User-ID attached (see below). The old
			// localStorage User-ID seed was removed: uid/imid rotate per session, so a persisted
			// value is a STALE prior-session id that mismatches search_submission's current id.
			window.gtag('js', new Date());
			window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
		}
	}, []);

	useEffect(() => {
		if (typeof window === 'undefined' || typeof window.gtag !== 'function' || !router.isReady) return;

		// Fire the initial GA4 page_view once. Attach the current-session User-ID (from init()'s
		// uid/imid props) if it is already available so page_view can join with search_submission;
		// otherwise fire it WITHOUT user_id — page_view must NOT be deferred/dropped, it has to
		// fire on /passthru redirects + fast bounces (~12ms) that a uid gate would lose. init()'s
		// own gtag('set',{user_id}) backfills every later event. The FB PageView is fired from the
		// pixel snippet itself (fbq('track','PageView')) — firing it here was unreliable because
		// fbq isn't defined until the gated <FBPixelScript> mounts (isSiteLoaded).
		if (!pageViewFiredRef.current) {
			pageViewFiredRef.current = true;
			const userId = uid && imid ? `${uid}${imid}` : undefined;
			if (userId) {
				emitGAEventSetUserId(userId);
				emitFBEventSetUserId(userId);
			}
			window.gtag('event', 'page_view');
		}
	}, [router.pathname, router.isReady, uid, imid]);

	if (!isSiteLoaded) return null;

	const fbPixelId = process.env.NEXT_PUBLIC_FB_PIXEL_ID;
	const externalId = uid && imid ? `${uid}${imid}` : undefined;

	return (
		<>
			<Script
				id="gtag-js"
				strategy="afterInteractive"
				src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
			/>
			<FBPixelScript pixelId={fbPixelId} externalId={externalId} />
			<Script
				id="googleads"
				data-ad-client="ca-pub-2732183997877262"
				async
				src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"
			/>
		</>
	);
};

export default RenderGA;
